<script setup lang="ts">
import { VueDatePicker } from '@vuepic/vue-datepicker'
import { format, isValid, parse } from 'date-fns'
import { fr } from 'date-fns/locale'
import dayjs, { Dayjs } from 'dayjs'
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { capitalize } from '../../misc/string-utils'

const DATE_FORMAT = 'dd/MM/yyyy'

const startDate = defineModel<Dayjs | null>('startDate', { default: null })
const endDate = defineModel<Dayjs | null>('endDate', { default: null })
const emit = defineEmits<{
  'update:range': [start: Dayjs | null, end: Dayjs | null]
}>()

const { t } = useI18n()

function formatDate(date: Dayjs | null) {
  return date ? format(date.toDate(), DATE_FORMAT) : ''
}

// The calendar popup is driven by startDate/endDate directly, but each text
// input needs its own draft string so a half-typed date isn't reparsed on
// every keystroke -- only committed on Enter/blur.
const startText = ref(formatDate(startDate.value))
const endText = ref(formatDate(endDate.value))

watch(startDate, (value) => {
  startText.value = formatDate(value)
})
watch(endDate, (value) => {
  endText.value = formatDate(value)
})

const rangeValue = computed<[Date, Date] | null>(() => {
  if (!startDate.value || !endDate.value) {
    return null
  }
  return [startDate.value.toDate(), endDate.value.toDate()]
})

const timeConfig = { enableTimePicker: false }
// Open on last month + current month rather than the library's default of
// current month + next month, since picked dates are almost always in the past.
const initialDisplayDate = dayjs().subtract(1, 'month').toDate()

const datePickerRef = ref<InstanceType<typeof VueDatePicker> | null>(null)
// Which input was clicked to open the calendar: drives which month the
// calendar opens on, and which input receives the first picked date (see
// handleRangeStart).
const activeField = ref<'start' | 'end' | null>(null)
// Set to the field/date once the first date of a new range has been picked
// and shown provisionally in its input (see handleRangeStart), so that
// closing the calendar right after -- without picking a second date -- still
// commits that single change (see handleClosed). Also tells
// handleStartTextChange/handleEndTextChange to leave that field alone: its
// displayed text is a calendar pick in progress, not a typed edit to commit.
const pendingPartialCommit = ref<{ field: 'start' | 'end', date: Dayjs } | null>(null)

// The library defaults range.partialRange to true, meaning it treats a
// single picked date as a valid, complete selection and auto-applies (closes
// the menu) right after the first click. We require both dates before
// auto-applying.
const rangeConfig = { partialRange: false }

const calendarStartDate = computed(() => {
  if (activeField.value === 'start' && startDate.value) {
    return startDate.value.toDate()
  }
  if (activeField.value === 'end' && endDate.value) {
    return endDate.value.toDate()
  }
  return initialDisplayDate
})

function openFor(field: 'start' | 'end') {
  activeField.value = field
  // Wait for the updated calendarStartDate to reach the child's props before
  // opening, since openMenu() reads it synchronously.
  nextTick(() => {
    datePickerRef.value?.openMenu()
  })
}

function handleClosed() {
  activeField.value = null
  if (pendingPartialCommit.value) {
    // The menu was closed with only one date picked (e.g. a click outside):
    // commit that single change now rather than discarding it.
    const { field, date } = pendingPartialCommit.value
    pendingPartialCommit.value = null
    if (field === 'end') {
      commitRange(startDate.value, date)
    } else {
      commitRange(date, endDate.value)
    }
  }
}

function handleRangeStart(date: Date) {
  // First date of a new range picked: show it right away in whichever input
  // was clicked to open the calendar, without emitting update:range yet --
  // the menu stays open so a second date can still complete/adjust the range
  // (see handleClosed for an early close, and handleUpdate for the second
  // date). update:model-value itself doesn't fire for this first pick since
  // the range isn't complete yet (range.partialRange is false).
  const picked = dayjs(date)
  const field: 'start' | 'end' = activeField.value === 'end' ? 'end' : 'start'
  if (field === 'end') {
    endText.value = formatDate(picked)
  } else {
    startText.value = formatDate(picked)
  }
  pendingPartialCommit.value = { field, date: picked }
}

function handleUpdate(value: Date[] | null) {
  // Only reached once both dates of the range are picked (or the range is
  // cleared to null): the partial, single-date state is handled by
  // handleRangeStart above.
  pendingPartialCommit.value = null
  const newStart = value?.[0] ? dayjs(value[0]) : null
  const newEnd = value?.[1] ? dayjs(value[1]) : null
  commitRange(newStart, newEnd)
}

function commitRange(newStart: Dayjs | null, newEnd: Dayjs | null) {
  startDate.value = newStart
  endDate.value = newEnd
  // Also emitted as a single combined event so consumers that react to a date
  // range change (e.g. by refreshing a list) can do so once instead of twice
  // -- update:startDate/update:endDate above fire as two separate emits.
  emit('update:range', newStart, newEnd)
}

function parseTypedDate(text: string): Dayjs | null {
  const parsed = parse(text, DATE_FORMAT, new Date(), { locale: fr })
  return isValid(parsed) ? dayjs(parsed) : null
}

function handleStartTextChange() {
  if (pendingPartialCommit.value?.field === 'start') {
    // The input shows a calendar pick in progress, not a typed edit: this
    // fires on blur as a side effect of clicking a day in the calendar
    // (VueDatePicker doesn't preventDefault on it). Let handleClosed/
    // handleUpdate commit it instead, once the calendar interaction is done.
    return
  }
  const parsed = parseTypedDate(startText.value)
  if (!parsed || (endDate.value && parsed.isAfter(endDate.value, 'day'))) {
    // Invalid text, or would invert the range: revert rather than commit it.
    startText.value = formatDate(startDate.value)
    return
  }
  if (startDate.value && parsed.isSame(startDate.value, 'day')) {
    // No actual change (e.g. this blur only fired because a calendar click
    // stole focus, before any date was picked): nothing to commit.
    return
  }
  commitRange(parsed, endDate.value)
}

function handleEndTextChange() {
  if (pendingPartialCommit.value?.field === 'end') {
    return
  }
  const parsed = parseTypedDate(endText.value)
  if (!parsed || (startDate.value && parsed.isBefore(startDate.value, 'day'))) {
    endText.value = formatDate(endDate.value)
    return
  }
  if (endDate.value && parsed.isSame(endDate.value, 'day')) {
    return
  }
  commitRange(startDate.value, parsed)
}
</script>

<template>
  <VueDatePicker
    ref="datePickerRef"
    :model-value="rangeValue"
    :range="rangeConfig"
    :multi-calendars="2"
    :locale="fr"
    :time-config="timeConfig"
    :start-date="calendarStartDate"
    focus-start-date
    auto-apply
    class="sancare-date-range-picker"
    @update:model-value="handleUpdate"
    @range-start="handleRangeStart"
    @closed="handleClosed"
  >
    <template #trigger>
      <div class="sancare-date-range-picker__inputs">
        <label
          class="sancare-date-range-picker__label"
          @click.stop="openFor('start')"
        >{{ capitalize(t('time.from')) }}</label>
        <input
          v-model="startText"
          type="text"
          class="form-control sancare-date-range-picker__input"
          @click.stop="openFor('start')"
          @keydown.enter="handleStartTextChange"
          @blur="handleStartTextChange"
        >
        <label
          class="sancare-date-range-picker__label"
          @click.stop="openFor('end')"
        >{{ t('time.to') }}</label>
        <input
          v-model="endText"
          type="text"
          class="form-control sancare-date-range-picker__input"
          @click.stop="openFor('end')"
          @keydown.enter="handleEndTextChange"
          @blur="handleEndTextChange"
        >
      </div>
    </template>
  </VueDatePicker>
</template>

<style lang="scss" scoped>
.sancare-date-range-picker__inputs {
  display: flex;
  align-items: center;
  gap: .5rem;
}

.sancare-date-range-picker__input {
  // Fixed to the exact length of the 'dd/MM/yyyy' format so the input never
  // needs to be measured/resized at runtime.
  box-sizing: content-box;
  width: 10ch;
  cursor: pointer;
}
</style>
